//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Button, Grid, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent, Typography } from '@mui/material'
import React, { ReactElement, useEffect } from 'react'

import { theme } from '@react/components/theme'

import { useOtChainState, useOtDispatch, useOtSelector } from '@react/redux/store'
import { dynamicTransferDepositTx } from '@react/signedTxs/dynamicTransferDeposit'
import { EXCHANGE_RATE_ONE } from '@shared/Enums'
import { PoolDynamicOverviewCTO } from '@shared/PoolDynamicCTO'
import { DynamicDepositActivityCTO } from '@shared/PoolEventCTO'
import { EthAddr, PoolAccountAccessPair } from '@shared/SchemaTypes'
import { fetchPoolParticipants } from '@react/fetchers/PoolFetchers'
import { logOT } from '@shared/loggingShared'

export default function DynamicTransferDepositForm(props: {
  lenderAddr: EthAddr
  activity: DynamicDepositActivityCTO
  symbol: string
  liquidityTokenSymbol: string
}): ReactElement {
  const { lenderAddr, activity } = props
  const { poolAddr } = activity

  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    chainConfiguration: { providerReadOnly },
  } = useOtChainState()
  const poolCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr] as PoolDynamicOverviewCTO)

  const dispatch = useOtDispatch()

  const [selectedParticipant, setSelectedParticipant] = React.useState<EthAddr>(lenderAddr)

  const [participants, setParticipants] = React.useState<PoolAccountAccessPair[]>([
    { accountAddr: lenderAddr, poolAddr },
  ])
  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const sx = {
    backgroundColor: theme.palette.fg.main,
    color: '#FFFFFF',
    padding: '11px 16px 11px 16px',
    marginLeft: '8px',
    borderRadius: '6px',
    marginTop: 2,
  }
  useEffect(() => {
    if (providerReadOnly && poolAddr) {
      fetchPoolParticipants(serverApiBaseUrl, restAPIKey, poolAddr)
        .then((_participants) => {
          logOT.debug('all users', _participants)
          setParticipants(_participants)
        })
        .catch((error) => {
          logOT.error(error)
        })
    }
  }, [providerReadOnly, poolAddr])

  const transferDepositAction = (): void => {
    setIsSubmitted(true)
    // const assets = BigInt(Math.floor(Number(assetValue.replace(/,/g, '')) * 1e6))
    dispatch(
      dynamicTransferDepositTx({
        poolAddr,
        lenderAddr: selectedParticipant,
        assets: activity.assets,
        txHash: activity.txHash,
      })
    )
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }
  const disableButton = false
  logOT.info('PoolCTR', poolCTO)
  const tokens = (BigInt(activity.assets) * EXCHANGE_RATE_ONE) / BigInt(poolCTO.exchangeRate)

  return (
    <Grid container sx={{ marginTop: 2 }}>
      <Grid item xs={12}>
        <InputLabel id="asset-label">Lender</InputLabel>
        <Select
          labelId="asset-label"
          id="asset-select"
          input={<OutlinedInput id="asset-select" label="Asset" />}
          value={selectedParticipant}
          renderValue={(value: string) => {
            return (
              <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                {value}
              </Typography>
            )
          }}
          onChange={(event: SelectChangeEvent) => {
            logOT.info('event.target.value', event.target.value)
            setSelectedParticipant(event.target.value as string)
          }}
        >
          {participants.map((p: PoolAccountAccessPair) => (
            <MenuItem
              value={p.accountAddr}
              key={`asset-option-${p.accountAddr}`}
              style={{
                paddingLeft: 12,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}
            >
              <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                {p.accountAddr}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </Grid>

      <Grid item xs={12}>
        Assets {activity.assets.toString()} {poolCTO.liquidityTokenSymbol}
      </Grid>

      <Grid item xs={12}>
        ExchangeRate {poolCTO.exchangeRate}
      </Grid>
      <Grid item xs={12}>
        tokens {tokens.toString()} {props.symbol}
      </Grid>

      <Grid item xs={12}>
        txHash {activity.txHash}
      </Grid>

      <Grid item xs={12} sm={12}>
        <Button
          disabled={isSubmitted || disableButton}
          sx={sx}
          onClick={() => {
            transferDepositAction()
          }}
          variant={'contained'}
        >
          <Typography variant={'buttonBold'}>Approve and Mint</Typography>
        </Button>
      </Grid>
      {disableButton && (
        <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
          Disabled
        </Typography>
      )}
    </Grid>
  )
}
